import { useMemo } from 'react';
import { MdCheckCircle, MdWarningAmber } from 'react-icons/md';
import { ModelBrandEquity, ModelCommerce, Warning600, Warning700 } from '@src/utils/colors';
import { DataQaChart, SeriesConfig } from '../components/DataQaChart';
import { GroundTruthSqlPanel } from '../components/GroundTruthSqlPanel';
import { KpiBar, KpiTileData } from '../components/KpiBar';
import { ChannelConfig, ChannelStats } from '../data-qa-types';
import { ChannelAnalysis, platformField } from '../media-inputs-utils';

const BPM_COLOR = Warning600;
// One hue per platform, cycled if a channel somehow has more platforms than colors --
// MMM/BPM for the same platform share a color (solid vs dashed), so a platform's own
// pair of lines is visually grouped the way channel-level MMM/BPM already are.
const PLATFORM_COLORS = [
  '#8b5cf6',
  '#1f77b4',
  '#059669',
  '#dc2626',
  Warning700,
  ModelCommerce,
  ModelBrandEquity,
  '#65a30d'
];

// Builds one (or two, with a BPM overlay) series per platform for a channel with more
// than one platform -- see ChannelCard's hasPlatformBreakout. Mirrors SPEND_SERIES/
// IMPRESSIONS_SERIES's shape but generated per-platform instead of hardcoded, reading
// the `${metric}__${platformKey}`/`bpm${Metric}__${platformKey}` dataKeys flattenChannel
// adds onto each week when platformKeys is non-empty.
const buildPlatformSeries = (
  platforms: { key: string; label: string }[],
  metric: 'spend' | 'impressions',
  format: 'currency' | 'number',
  hasBpm: boolean
): SeriesConfig[] => {
  const metricLabel = metric === 'spend' ? 'Spend' : 'Impressions';
  const bpmMetricKey = metric === 'spend' ? 'bpmSpend' : 'bpmImpressions';
  return platforms.flatMap((platform, i) => {
    const color = PLATFORM_COLORS[i % PLATFORM_COLORS.length];
    const mmmSeries: SeriesConfig = {
      dataKey: `${metric}__${platform.key}`,
      label: `${platform.label} ${metricLabel}`,
      color,
      format,
      axis: 'left'
    };
    if (!hasBpm) return [mmmSeries];
    return [
      mmmSeries,
      {
        dataKey: `${bpmMetricKey}__${platform.key}`,
        label: `${platform.label} BPM ${metricLabel}`,
        color,
        format,
        axis: 'left',
        dashed: true
      }
    ];
  });
};

// Picks the per-platform breakout, the full MMM+BPM pair, or just the MMM line --
// whichever applies for this channel/metric (see ChannelCard's hasPlatformBreakout).
const seriesForChart = (
  hasPlatformBreakout: boolean,
  hasBpm: boolean,
  platforms: { key: string; label: string }[],
  metric: 'spend' | 'impressions',
  format: 'currency' | 'number',
  fullSeries: SeriesConfig[]
): SeriesConfig[] => {
  if (hasPlatformBreakout) return buildPlatformSeries(platforms, metric, format, hasBpm);
  return hasBpm ? fullSeries : fullSeries.slice(0, 1);
};

const SPEND_SERIES: SeriesConfig[] = [
  { dataKey: 'spend', label: 'MMM Spend', color: '#8b5cf6', format: 'currency', axis: 'left' },
  {
    dataKey: 'bpmSpend',
    label: 'BPM Spend',
    color: BPM_COLOR,
    format: 'currency',
    axis: 'left',
    dashed: true
  }
];
const IMPRESSIONS_SERIES: SeriesConfig[] = [
  {
    dataKey: 'impressions',
    label: 'MMM Impressions',
    color: '#1f77b4',
    format: 'number',
    axis: 'left'
  },
  {
    dataKey: 'bpmImpressions',
    label: 'BPM Impressions',
    color: BPM_COLOR,
    format: 'number',
    axis: 'left',
    dashed: true
  }
];

const money0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const money2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const int0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

interface ChannelPanelProps {
  channel: ChannelConfig;
  // Computed once by the parent (analyzeChannel), which also derives this channel's pill
  // indicator from it -- so the charts and the indicator can't disagree.
  analysis: ChannelAnalysis;
  stats?: ChannelStats;
  reviewGranularity?: string;
  bookmarkWeekIso?: string;
  isColdStart: boolean;
  company: string;
  // This channel's own raw feature_id(s) (cross-referenced from config.featureMapping
  // by the parent) -- only meaningful, and only passed to GroundTruthSqlPanel, when
  // this channel is a demand-group split (channel.label !== channel.rawLabel). See
  // ChannelConfig.rawLabel's own doc comment for why a split channel needs this.
  featureIds: string[];
}

// One channel's charts, KPIs, compare-to-canonical panel and delivery issues, as shown on
// the Media Inputs tab.
export const ChannelPanel = ({
  channel,
  analysis,
  stats,
  reviewGranularity,
  bookmarkWeekIso,
  isColdStart,
  company,
  featureIds
}: ChannelPanelProps) => {
  const {
    chartWeeks,
    activePlatforms,
    hasPlatformBreakout,
    platformKeys,
    hasImpressions,
    corrStrong,
    hasBpm,
    spendRanges,
    impressionsRanges,
    issues
  } = analysis;
  const groundTruthWeeks = useMemo(
    () =>
      chartWeeks.map(w => ({
        weekIso: w.weekIso,
        mmmSpend: w.spend,
        mmmImpressions: w.impressions,
        bpmSpend: w.bpmSpend,
        bpmImpressions: w.bpmImpressions,
        ...(hasPlatformBreakout
          ? {
              perPlatform: Object.fromEntries(
                platformKeys.map(platformKey => [
                  platformKey,
                  {
                    spend: platformField(w, `spend__${platformKey}`) ?? 0,
                    impressions: platformField(w, `impressions__${platformKey}`) ?? 0
                  }
                ])
              )
            }
          : {})
      })),
    [chartWeeks, hasPlatformBreakout, platformKeys]
  );
  const kpiTiles: KpiTileData[] = [
    {
      label: 'Spend',
      note: '(weekly avg)',
      value: money0.format(stats?.avgSpend ?? 0),
      unit: '/wk'
    },
    {
      label: 'Impressions',
      note: '(weekly avg)',
      value: hasImpressions ? int0.format(stats?.avgImpressions ?? 0) : 'N/A',
      ...(hasImpressions ? { unit: '/wk' } : {})
    },
    {
      label: 'CPM',
      note: '(blended)',
      value: hasImpressions ? money2.format(stats?.avgCpm ?? 0) : 'N/A'
    },
    {
      label: 'Correlation (spend vs. impressions)',
      value: hasImpressions ? (stats?.platformCorrelation ?? 0).toFixed(3) : 'N/A',
      badge: hasImpressions
        ? {
            text: corrStrong ? 'Strong Correlation' : 'Weak Correlation',
            kind: corrStrong ? 'strong' : 'weak'
          }
        : undefined
    }
  ];

  return (
    <div className='data-qa-channel'>
      <div className='data-qa-channel__head data-qa-channel__head--static'>
        <span className='data-qa-channel__title'>
          {channel.label} | Weekly Spend and Impressions
        </span>
      </div>
      <div className='data-qa-channel__body'>
        <div className='data-qa-channel__subtitle'>
          Model spend and impressions as compared to the canonical Bliss Point database
        </div>
        <KpiBar tiles={kpiTiles} />
        <DataQaChart
          title={`${channel.label} — Spend by Platform`}
          weeks={chartWeeks}
          series={seriesForChart(
            hasPlatformBreakout,
            hasBpm,
            activePlatforms,
            'spend',
            'currency',
            SPEND_SERIES
          )}
          divergentRanges={spendRanges}
          bookmarkWeekIso={bookmarkWeekIso}
          isColdStart={isColdStart}
          height={260}
          hideLegend={!hasBpm && !hasPlatformBreakout}
        />
        {hasImpressions && (
          <DataQaChart
            title={`${channel.label} — Impressions by Platform`}
            weeks={chartWeeks}
            series={seriesForChart(
              hasPlatformBreakout,
              hasBpm,
              activePlatforms,
              'impressions',
              'number',
              IMPRESSIONS_SERIES
            )}
            divergentRanges={impressionsRanges}
            bookmarkWeekIso={bookmarkWeekIso}
            isColdStart={isColdStart}
            height={260}
            hideLegend={!hasBpm && !hasPlatformBreakout}
          />
        )}
        {hasBpm && (
          <GroundTruthSqlPanel
            company={company}
            weeks={groundTruthWeeks}
            // rawLabel, NOT label -- Redshift's Mapped Channel value has no demand-
            // group suffix even when this channel is a split entry.
            channelLabel={channel.rawLabel}
            reviewGranularity={reviewGranularity}
            platforms={hasPlatformBreakout ? activePlatforms : undefined}
            featureIds={channel.label !== channel.rawLabel ? featureIds : undefined}
          />
        )}
        {issues.length === 0 ? (
          <div className='data-qa-channel__issues data-qa-channel__issues--clear'>
            <MdCheckCircle className='data-qa-channel__issues-icon' />
            <div>
              <div className='data-qa-channel__issues-title'>No Delivery Issues Detected</div>
              <div className='data-qa-channel__issues-sub'>
                No spend gaps or low correlation detected for {channel.label} — see Compare to
                canonical (BPM) above for data-quality checks.
              </div>
            </div>
          </div>
        ) : (
          <div className='data-qa-channel__issues data-qa-channel__issues--warn'>
            <MdWarningAmber />
            <span>{issues.join(' · ')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
